/**
 * Aircraft classes and the fleet count the monthly fee is based on.
 *
 * Owner decision (29.10.2026): a club pays per registered aircraft, by class —
 * Motorflugzeug, Motorsegler, Segelflugzeug, UL — and the month counts the
 * club's ACTIVE aircraft on the 1st of that month, 00:00 Europe/Berlin
 * (yearly payers: on the 1st of the contract year's first billed month —
 * billing.ts, which decides that month).
 *
 * The class belongs to the TYPE (`aircraft_types.aircraft_class`, 0025): the
 * model decides what an aircraft is. NULL means unknown — a club's own type
 * from before 0025 — and is counted separately so billing can flag it; it is
 * never guessed.
 *
 * Pure, and without `Date` for the calendar (CLAUDE.md, "Kalender und Geld
 * ohne Date"): day numbers after Hinnant, the EU summer-time rule by hand.
 */

export const AIRCRAFT_CLASSES = ['motor', 'tmg', 'glider', 'ul'] as const;
export type AircraftClass = (typeof AIRCRAFT_CLASSES)[number];

export function isAircraftClass(value: unknown): value is AircraftClass {
  return typeof value === 'string' && (AIRCRAFT_CLASSES as readonly string[]).includes(value);
}

// ------------------------------------------------------------------ calendar

/** Days since 1970-01-01 of a proleptic Gregorian date (Hinnant, `days_from_civil`). */
export function dayNumber(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const mp = (month + 9) % 12;
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/** 0 = Sunday … 6 = Saturday. 1970-01-01 was a Thursday. */
function weekday(days: number): number {
  return (((days + 4) % 7) + 7) % 7;
}

function lastSunday(year: number, month: 3 | 10): number {
  const last = dayNumber(year, month, 31);
  return last - weekday(last);
}

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/**
 * The UTC instant (epoch ms) of 00:00 Europe/Berlin on that calendar day.
 *
 * Summer time runs from the last Sunday of March, 01:00 UTC, to the last
 * Sunday of October, 01:00 UTC. Local midnight lies before the switch on both
 * Sundays (23:00 / 22:00 UTC the evening before), so a day is summer exactly
 * when it is after the last Sunday of March and not after the last Sunday of
 * October.
 */
export function berlinMidnightUtc(year: number, month: number, day: number): number {
  const d = dayNumber(year, month, day);
  const summer = d > lastSunday(year, 3) && d <= lastSunday(year, 10);
  return d * DAY_MS - (summer ? 2 : 1) * HOUR_MS;
}

export interface BillingMonth {
  readonly year: number;
  readonly month: number;
}

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

/** `YYYY-MM` → month, or null for anything else (2000-01 … 2999-12). */
export function parseBillingMonth(value: unknown): BillingMonth | null {
  if (typeof value !== 'string') return null;
  const m = MONTH_PATTERN.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (year < 2000 || month < 1 || month > 12) return null;
  return { year, month };
}

export function formatBillingMonth({ year, month }: BillingMonth): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** The instant the month's fee is counted at: the 1st, 00:00 Europe/Berlin. */
export function billingInstant(month: BillingMonth): number {
  return berlinMidnightUtc(month.year, month.month, 1);
}

// ------------------------------------------------------------------ counting

/**
 * One version of one aircraft row: what the temporal table held from
 * `validFrom` (inclusive) to `validTo` (exclusive, null = still current).
 * Times are epoch ms (UTC).
 */
export interface FleetVersion {
  readonly aircraftId: string;
  /**
   * The registration, when the caller counts several tenants of one contract
   * together (0026): the same aircraft entered in two departments is one
   * aircraft. Never leaves the count (a platform DTO carries no registration).
   */
  readonly registration?: string;
  readonly isActive: boolean;
  /** Soft-deleted (`deleted_at` set) in this version. */
  readonly deleted: boolean;
  /** The App Store review aircraft (`is_demo`): never a club's real fleet. */
  readonly isDemo: boolean;
  readonly typeCode: string;
  /** The class of the version's type; null = unknown. */
  readonly aircraftClass: AircraftClass | null;
  readonly validFrom: number;
  readonly validTo: number | null;
}

export type ClassCounts = Record<AircraftClass, number>;

export interface FleetCount {
  readonly counts: ClassCounts;
  /** Counted aircraft whose type has no class: billing must ask, not guess. */
  readonly unclassified: number;
  /** Codes of those types, sorted. */
  readonly unclassifiedTypes: readonly string[];
  /** All counted aircraft, classified or not. */
  readonly total: number;
  /** Registrations that held in more than one row at the instant — counted once (0026). */
  readonly duplicates: number;
}

export function emptyClassCounts(): ClassCounts {
  return { motor: 0, tmg: 0, glider: 0, ul: 0 };
}

/** Did this version hold at `at`? Half-open: a row that starts at `at` counts, one that ends at `at` does not. */
export function versionHoldsAt(
  v: Pick<FleetVersion, 'validFrom' | 'validTo'>,
  at: number,
): boolean {
  return v.validFrom <= at && (v.validTo === null || at < v.validTo);
}

/**
 * The club's billable fleet at instant `at`: every aircraft whose version at
 * that instant is active, not deleted and not the review demo — once, even if
 * the caller passes overlapping rows, and once per registration when the
 * versions carry one (a contract's departments counted together, 0026: the
 * API refuses a registration twice in one contract, this is the backstop and
 * says so in `duplicates`).
 */
export function countFleetAt(versions: readonly FleetVersion[], at: number): FleetCount {
  const counts = emptyClassCounts();
  const seen = new Set<string>();
  const registrations = new Set<string>();
  const unknown = new Set<string>();
  let unclassified = 0;
  let duplicates = 0;
  for (const v of versions) {
    if (!versionHoldsAt(v, at)) continue;
    if (!v.isActive || v.deleted || v.isDemo) continue;
    if (seen.has(v.aircraftId)) continue;
    seen.add(v.aircraftId);
    if (v.registration !== undefined) {
      const reg = v.registration.toUpperCase();
      if (registrations.has(reg)) {
        duplicates += 1;
        continue;
      }
      registrations.add(reg);
    }
    if (v.aircraftClass === null) {
      unclassified += 1;
      unknown.add(v.typeCode);
    } else {
      counts[v.aircraftClass] += 1;
    }
  }
  return {
    counts,
    unclassified,
    unclassifiedTypes: [...unknown].sort(),
    total: seen.size - duplicates,
    duplicates,
  };
}
